import { OUTBOX_MAX_AGE_HOURS } from '@helpdesk/shared';
import { useQuery } from '@tanstack/react-query';
import { getJson } from '../api/client.ts';
import type { PrivacyRetention } from '../api/types.ts';
import { PublicShell } from '../components/layout.tsx';
import { LOGOUT_PENDING_NOTE, usePendingLogout } from '../lib/logout.ts';

/** Nach dem Abmelden (§9.5): klare Seite für die nächste Person am Tablet. */
export function LoggedOutPage() {
  const pending = usePendingLogout();
  return (
    <PublicShell>
      <div className="ad-card space-y-4 text-center">
        <h1 className="text-xl">{pending ? 'Abmeldung steht aus' : 'Abgemeldet'}</h1>
        <p>{pending ? LOGOUT_PENDING_NOTE : 'Sie sind abgemeldet.'}</p>
        <a className="ad-big-button" href="/">
          Nächste Person anmelden
        </a>
      </div>
    </PublicShell>
  );
}

export function HelpPage() {
  return (
    <PublicShell>
      <article className="ad-card space-y-4 text-[15px] leading-relaxed">
        <h1 className="text-2xl">Hilfe</h1>
        <section>
          <h2 className="mb-1 text-lg">Störung melden in 20 Sekunden</h2>
          <ol className="list-decimal space-y-1 pl-5">
            <li>QR-Code an der Maschine mit der Kamera-App scannen.</li>
            <li>„Störung melden“ antippen.</li>
            <li>Eine Kachel wählen: Sicherheit, Stillstand, Einschränkung oder Sonstiges.</li>
            <li>Foto machen oder einen Satz schreiben. Eines genügt.</li>
            <li>Ohne Anmeldung: Namen eintragen. Absenden, fertig.</li>
          </ol>
          <p className="mt-2">Den Stand Ihrer Meldung sehen Sie, wenn Sie den QR-Code erneut scannen.</p>
        </section>
        <section>
          <h2 className="mb-1 text-lg">Per E-Mail</h2>
          <p>Eine Mail an das Instandhaltungspostfach wird automatisch ein Ticket. Antworten Sie auf Mails zu einem Ticket immer mit „Antworten“. Dann landet Ihre Nachricht im richtigen Ticket. Antworten auf Sammelmails und andere Mails ohne Ticket werden nicht gelesen.</p>
        </section>
        <section>
          <h2 className="mb-1 text-lg">App auf dem Handy installieren</h2>
          <p>
            <strong>Android (Chrome/Edge):</strong> Menü ⋮, dann „App installieren“. <strong>iPhone (Safari):</strong> Teilen-Symbol, dann „Zum Home-Bildschirm“. Push-Benachrichtigungen gibt es auf dem
            iPhone nur in der installierten App.
          </p>
        </section>
        <section>
          <h2 className="mb-1 text-lg">Ohne Netz</h2>
          <p>
            Kein WLAN in der Halle? Ganz ohne Netz wird die Meldung auf dem Handy gespeichert und gesendet, sobald die App oder die Seite offen ist und wieder Netz hat. Das gilt auch, wenn das Hallen-WLAN
            wegfällt, während das Formular offen ist. Das Helpdesk muss dafür auf dem Handy schon einmal mit Netz offen gewesen sein. Ohne Anmeldung geht Melden nur im Hallen-WLAN oder ganz ohne Netz: Mit
            Mobilfunk oder einem anderen WLAN erscheint statt des Formulars die Bitte, sich anzumelden. Gesendet wird ohne Anmeldung nur im Hallen-WLAN, mit Konto nur, wenn Sie angemeldet sind. Erst die
            Ticketnummer bestätigt den Eingang. Nach {OUTBOX_MAX_AGE_HOURS} Stunden wird eine nicht gesendete Meldung vom Handy gelöscht. Bei einer dringenden Störung bitte die Instandhaltung direkt
            ansprechen.
          </p>
        </section>
        <section>
          <h2 className="mb-1 text-lg">Fotos</h2>
          <p>Bitte nur die Maschine fotografieren, keine Personen. Fotos werden verkleinert, Standortdaten entfernt.</p>
        </section>
      </article>
    </PublicShell>
  );
}

/** Die Fristen sind einstellbar, auch auf einen Monat. */
const months = (count: number) => (count === 1 ? '1 Monat' : `${count} Monaten`);

/**
 * Datenschutzhinweise (F47, Art. 13/14 DSGVO) – ohne Anmeldung erreichbar.
 * Der Text ist ein Entwurf und vor dem Pilot mit dem Datenschutzbeauftragten
 * abzustimmen (Plan §14, §17 Nr. 6).
 */
export function PrivacyPage() {
  const retention = useQuery({ queryKey: ['privacy', 'retention'], queryFn: () => getJson<PrivacyRetention>('/api/privacy/retention') });
  // Die Seite muss in jedem Zustand stimmen: Ohne gültige Antwort ist offen, ob Gruppe B läuft, dann bleibt nur die Bedingung.
  // Die Zahlen sind dann die Vorgaben aus `settings.ts`.
  const groupB = retention.isSuccess ? retention.data : null;

  return (
    <PublicShell>
      <article className="ad-card space-y-4 text-[15px] leading-relaxed">
        <h1 className="text-2xl">Datenschutzhinweise</h1>
        <p className="text-sm text-[var(--text-muted)]">Entwurf. Vor dem Pilotbetrieb mit dem Datenschutzbeauftragten abstimmen.</p>
        <section>
          <h2 className="mb-1 text-lg">Wozu</h2>
          <p>
            Das Instandhaltungs-Helpdesk dient dazu, Störungen an Maschinen und Anlagen der Arnold AG zu melden, zu bearbeiten und zu dokumentieren. Es ist kein Werkzeug zur Leistungs- oder
            Verhaltenskontrolle: Auswertungen gibt es nur je Maschine, Bereich, Standort, Ursache, Priorität und Zeitraum, nie je Person.
          </p>
        </section>
        <section>
          <h2 className="mb-1 text-lg">Welche Daten</h2>
          <ul className="list-disc space-y-1 pl-5">
            <li>Beschäftigte mit Microsoft-Konto: Name und Firmen-Mail aus Microsoft Entra ID, Rolle und Standort aus Entra ID oder von der Systemverwaltung, Anmeldezeitpunkte.</li>
            <li>
              Beschäftigte mit Konto ohne Microsoft 365: Name, Benutzername, Rolle, Standort und optional eine Mailadresse, von der Systemverwaltung erfasst, dazu das Passwort nur als nicht lesbarer
              Prüfwert (Hash), Anmeldezeitpunkte, Fehlversuche und Sperrzeit.
            </li>
            <li>Meldungen: Maschine, Beschreibung, Fotos (ohne Standortdaten), Zeitpunkte, Name des Melders.</li>
            <li>Schnellmeldung ohne Anmeldung: der angegebene Name, optional eine Firmen-Mail; die IP-Adresse nur als nicht lesbarer Prüfwert (Hash) für 24 Stunden (Missbrauchsschutz).</li>
            <li>
              Bearbeitung von Meldungen: wem ein Ticket zugewiesen ist, wer hinzugezogen wurde, wer es angenommen, bearbeitet oder kommentiert hat, wer Dateien angefügt oder gelöscht und wer die
              Maschine wieder freigegeben hat, jeweils mit Zeitpunkt, dazu der Text der Kommentare (Vorgangsnachweis).
            </li>
            <li>Ein- und ausgehende E-Mails des Instandhaltungspostfachs: Absender, Empfänger und Kopie, Betreff, Inhalt, Anhänge, Zeitpunkte, ausgewählte Kopfzeilen.</li>
            <li>
              Externe Absender und Kontakte: Mailadresse und Name aus der Mail oder von der Instandhaltung eingetragen, dazu Firma, Freigabe und Sperre von der Instandhaltung, Zeitpunkte des ersten und
              letzten Kontakts (Art. 14 DSGVO).
            </li>
            <li>Benachrichtigungen per Mail und Push: Empfänger, Text, Versandzeitpunkt.</li>
            <li>
              Verwaltungsprotokoll: wer Konten, Rollen, Einstellungen, Stammdaten oder Kontaktfreigaben geändert, Daten exportiert oder dieses Protokoll eingesehen hat, mit Zeitpunkt, teils mit altem und
              neuem Wert.
            </li>
          </ul>
        </section>
        <section>
          <h2 className="mb-1 text-lg">Wie lange</h2>
          <div className="space-y-2">
            <p>
              Technische Daten (Zähler gegen Missbrauch 24 h, Benachrichtigungen und versendete Mails 90 Tage, gelöschte Fotos und Dokumente 30 Tage) werden automatisch gelöscht. Von einer versendeten
              Mail bleiben die Kopie im Postfach und ein Vermerk im Ticketverlauf mit Empfänger und Betreff. Die Kopie wird mit den Mail-Inhalten gelöscht. Der Vermerk bleibt am Ticket, nur seine
              Mailadresse wird mit dem Personenbezug entfernt. Wurde ein Kommentar per Mail versendet, bleibt sein Text als Kommentar am Ticket.
            </p>
            <p>
              Mail-Inhalte abgeschlossener oder abgelehnter Tickets werden nach {months(groupB?.mailBodyMonths ?? 12)} gelöscht. Der Personenbezug dieser Tickets wird nach{' '}
              {months(groupB?.ticketPersonalMonths ?? 24)} pseudonymisiert, nach Maßgabe der Betriebsvereinbarung. Der Inhalt von Spam und abgelehnten Tickets ohne Maschine wird nach{' '}
              {months(groupB?.mailBodyMonths ?? 12)} gelöscht.
              {!groupB?.personalEnabled && ' Diese Fristen gelten, sobald die Systemverwaltung die Löschregeln eingeschaltet hat.'}
              {groupB?.personalEnabled === false && ' Das ist derzeit nicht der Fall: Bis dahin bleiben Mail-Inhalte und Personenbezug gespeichert.'}
            </p>
            <p>
              Tickets mit Titel, Beschreibung, Kommentaren, Ursache, Maßnahme, Fotos und Anhängen sowie Prüfberichte bleiben als Maschinenhistorie gespeichert, auch wenn der Titel der Betreff einer Mail
              ist. Text, der per Mail eingegangen ist, zählt dagegen zu den Mail-Inhalten. Eine Löschung nach Stilllegung der Maschine ist nicht vorgesehen.
            </p>
          </div>
        </section>
        <section>
          <h2 className="mb-1 text-lg">Wo</h2>
          <p>
            Die Daten liegen auf Servern der Arnold AG bzw. eines Hosting-Dienstleisters in Deutschland. Mail und die Anmeldung mit Microsoft-Konto laufen über Microsoft 365 der Arnold AG. Mit einem
            Konto ohne Microsoft 365 melden Sie sich direkt im Helpdesk an. Push-Nachrichten laufen verschlüsselt über den Push-Dienst Ihres Browsers (Apple, Google, Microsoft oder Mozilla).
          </p>
        </section>
        <section>
          <h2 className="mb-1 text-lg">Ansprechpartner</h2>
          <p>Fragen zum Datenschutz: Datenschutzbeauftragte(r) der Arnold AG. Auskunft, Berichtigung, Löschung: über die Systemverwaltung des Helpdesks.</p>
        </section>
      </article>
    </PublicShell>
  );
}
